import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './Planning.css';
import { Mail, Ban, Pencil, Palmtree, Lock, BookOpen, Calendar, Clock, User, Phone, MapPin, CheckCircle, XCircle, AlertTriangle, Save, Trash2, Plus, RefreshCw, Eye, ClipboardList } from 'lucide-react';

import { useRafraichissementAuto } from '../rafraichissementAuto';
function Planning({ userRole = 'agent', userId = null, allowedCommercialIds = null, onlyConfirmed = false, planningType = 'standard' }) {
  const [commerciaux, setCommerciaux] = useState([]);
  const [rdvs, setRdvs] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [stats, setStats] = useState(null);
  const [showCommercialModal, setShowCommercialModal] = useState(false);
  const [editingCommercial, setEditingCommercial] = useState(null);
  const [commercialForm, setCommercialForm] = useState({ nom: '', couleur: '', email: '' });
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSelections, setEmailSelections] = useState([]);

  // États pour la recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);

  // États pour les blocages
  const [blocages, setBlocages] = useState([]);
  const [showBlocageModal, setShowBlocageModal] = useState(false);
  const [showBlocageListModal, setShowBlocageListModal] = useState(false);
  const [editingBlocage, setEditingBlocage] = useState(null);
  const [blocageForm, setBlocageForm] = useState({
    commercial_id: '',
    date_debut: '',
    date_fin: '',
    heure_debut: '',
    heure_fin: '',
    motif: 'indisponible',
    description: ''
  });

  const [formData, setFormData] = useState({
    commercial_id: '',
    titre: '',
    description: '',
    client_nom: '',
    client_telephone: '',
    client_adresse: '',
    date_rdv: '',
    heure_debut: '09:00',
    heure_fin: '10:00',
    statut: 'planifie'
  });

  // Heures de travail (8h - 20h)
  const heures = [];
  for (let h = 8; h <= 20; h++) {
    heures.push(`${h.toString().padStart(2, '0')}:00`);
  }

  // Calculer les dates de la semaine
  const getWeekDates = (date) => {
    const week = [];
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + 1); // Lundi
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      week.push(d);
    }
    return week;
  };

  const weekDates = getWeekDates(currentWeek);
  const joursSemaine = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Mise a jour automatique de la page (voir src/rafraichissementAuto.js)
  useRafraichissementAuto(() => { fetchRdvs(true) }, { secondes: 60 });

  useEffect(() => {
    fetchCommerciaux();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchRdvs();
    fetchBlocages();
  }, [currentWeek]);

  const fetchCommerciaux = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/planning/commerciaux?type=${planningType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = allowedCommercialIds ? response.data.filter(c => allowedCommercialIds.includes(c.id)) : response.data;
      setCommerciaux(data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, commercial_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Erreur chargement commerciaux:', error);
    }
  };

  const fetchRdvs = async (silencieux = false) => {
    try {
      if (!silencieux) setLoading(true);
      const token = localStorage.getItem('token');
      const start = weekDates[0].toISOString().split('T')[0];
      const end = weekDates[6].toISOString().split('T')[0];

      const response = await axios.get(`/api/planning/rdv?start=${start}&end=${end}&type=${planningType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Si onlyConfirmed, ne montrer que les RDV confirmés (+ ceux créés par cet utilisateur)
      const data = onlyConfirmed
        ? response.data.filter(r => r.statut === 'confirme' || r.created_by === userId)
        : response.data;
      setRdvs(data);
    } catch (error) {
      console.error('Erreur chargement RDV:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/planning/stats?type=${planningType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const fetchBlocages = async () => {
    try {
      const token = localStorage.getItem('token');
      const start = weekDates[0].toISOString().split('T')[0];
      const end = weekDates[6].toISOString().split('T')[0];
      const response = await axios.get(`/api/planning/blocages?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocages(response.data);
    } catch (error) {
      console.error('Erreur chargement blocages:', error);
    }
  };

  // Rechercher des RDV
  const handleSearch = async () => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return;
    }

    try {
      setSearching(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/planning/rdv/search?q=${encodeURIComponent(searchTerm.trim())}&type=${planningType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Erreur recherche RDV:', error);
    } finally {
      setSearching(false);
    }
  };

  // Naviguer vers un RDV trouvé
  const goToRdv = (rdv) => {
    const rdvDate = new Date(rdv.date_rdv);
    setCurrentWeek(rdvDate);
    setShowSearchResults(false);
    setSearchTerm('');
    // Mettre en surbrillance le RDV sélectionné
    setTimeout(() => {
      setSelectedRdv(rdv);
      setShowEditModal(true);
    }, 500);
  };

  // Vérifier si un créneau est bloqué pour un commercial
  const isSlotBlocked = (commercialId, date, heure) => {
    const dateStr = date.toISOString().split('T')[0];
    const slotHour = parseInt(heure.split(':')[0]);

    return blocages.some(blocage => {
      // Vérifier si le blocage s'applique à ce commercial (ou à tous si commercial_id est null)
      if (blocage.commercial_id !== null && blocage.commercial_id !== commercialId) {
        return false;
      }

      // Vérifier si la date est dans la période du blocage
      const blocageStart = new Date(blocage.date_debut);
      const blocageEnd = new Date(blocage.date_fin);
      const currentDate = new Date(dateStr);

      if (currentDate < blocageStart || currentDate > blocageEnd) {
        return false;
      }

      // Si pas d'heure définie, c'est une journée entière bloquée
      if (!blocage.heure_debut || !blocage.heure_fin) {
        return true;
      }

      // Vérifier si l'heure est dans la plage bloquée
      const blocageHeureDebut = parseInt(blocage.heure_debut.substring(0, 2));
      const blocageHeureFin = parseInt(blocage.heure_fin.substring(0, 2));

      return slotHour >= blocageHeureDebut && slotHour < blocageHeureFin;
    });
  };

  // Obtenir le blocage pour un créneau
  const getBlockageForSlot = (commercialId, date, heure) => {
    const dateStr = date.toISOString().split('T')[0];
    const slotHour = parseInt(heure.split(':')[0]);

    return blocages.find(blocage => {
      if (blocage.commercial_id !== null && blocage.commercial_id !== commercialId) {
        return false;
      }

      const blocageStart = new Date(blocage.date_debut);
      const blocageEnd = new Date(blocage.date_fin);
      const currentDate = new Date(dateStr);

      if (currentDate < blocageStart || currentDate > blocageEnd) {
        return false;
      }

      if (!blocage.heure_debut || !blocage.heure_fin) {
        return true;
      }

      const blocageHeureDebut = parseInt(blocage.heure_debut.substring(0, 2));
      const blocageHeureFin = parseInt(blocage.heure_fin.substring(0, 2));

      return slotHour >= blocageHeureDebut && slotHour < blocageHeureFin;
    });
  };

  // Ouvrir le modal de création de blocage
  const openBlocageModal = (commercialId = '', dateStr = '') => {
    setBlocageForm({
      commercial_id: commercialId,
      date_debut: dateStr || new Date().toISOString().split('T')[0],
      date_fin: dateStr || new Date().toISOString().split('T')[0],
      heure_debut: '',
      heure_fin: '',
      motif: 'indisponible',
      description: ''
    });
    setEditingBlocage(null);
    setShowBlocageModal(true);
  };

  // Éditer un blocage existant
  const editBlocage = (blocage) => {
    setBlocageForm({
      commercial_id: blocage.commercial_id || '',
      date_debut: blocage.date_debut.split('T')[0],
      date_fin: blocage.date_fin.split('T')[0],
      heure_debut: blocage.heure_debut ? blocage.heure_debut.substring(0, 5) : '',
      heure_fin: blocage.heure_fin ? blocage.heure_fin.substring(0, 5) : '',
      motif: blocage.motif,
      description: blocage.description || ''
    });
    setEditingBlocage(blocage);
    setShowBlocageModal(true);
  };

  // Soumettre le formulaire de blocage
  const handleBlocageSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...blocageForm,
        commercial_id: blocageForm.commercial_id || null,
        heure_debut: blocageForm.heure_debut || null,
        heure_fin: blocageForm.heure_fin || null
      };

      if (editingBlocage) {
        await axios.put(`/api/planning/blocages/${editingBlocage.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/planning/blocages', data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowBlocageModal(false);
      fetchBlocages();
    } catch (error) {
      console.error('Erreur sauvegarde blocage:', error);
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  // Supprimer un blocage
  const deleteBlocage = async (id) => {
    if (!window.confirm('Supprimer ce blocage ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/planning/blocages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBlocages();
      setShowBlocageModal(false);
    } catch (error) {
      console.error('Erreur suppression blocage:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Labels pour les motifs de blocage
  const getMotifLabel = (motif) => {
    const labels = {
      'vacances': 'Vacances',
      'indisponible': 'Indisponible',
      'ferme': 'Fermé',
      'formation': 'Formation',
      'autre': 'Autre'
    };
    return labels[motif] || motif;
  };

  const openEmailModal = () => {
    // Initialiser les sélections avec les commerciaux
    const selections = commerciaux.map(c => ({
      id: c.id,
      nom: c.nom,
      email: c.email || '',
      selected: false,
      couleur: c.couleur
    }));
    setEmailSelections(selections);
    setShowEmailModal(true);
  };

  const toggleCommercialSelection = (id) => {
    setEmailSelections(prev => prev.map(s =>
      s.id === id ? { ...s, selected: !s.selected } : s
    ));
  };

  const updateCommercialEmail = (id, email) => {
    setEmailSelections(prev => prev.map(s =>
      s.id === id ? { ...s, email } : s
    ));
  };

  const sendSelectedEmails = async () => {
    const selected = emailSelections.filter(s => s.selected && s.email);

    if (selected.length === 0) {
      alert('Veuillez sélectionner au moins un commercial avec une adresse email');
      return;
    }

    setSendingEmails(true);
    setEmailResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/planning/send-daily-email', {
        commerciaux: selected.map(s => ({ id: s.id, email: s.email }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailResult(response.data);
      setShowEmailModal(false);
    } catch (error) {
      console.error('Erreur envoi emails:', error);
      setEmailResult({
        success: false,
        error: error.response?.data?.error || 'Erreur lors de l\'envoi des emails'
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const handleToday = () => {
    setCurrentWeek(new Date());
  };

  // Calculer l'heure de fin (+1h)
  const calculateEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHours = hours + 1;
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleAddRdv = (commercialId, date, heure) => {
    setFormData({
      commercial_id: commercialId,
      titre: '',
      description: '',
      client_nom: '',
      client_telephone: '',
      client_adresse: '',
      date_rdv: date,
      heure_debut: heure,
      heure_fin: calculateEndTime(heure),
      statut: 'planifie'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      // Calculer automatiquement l'heure de fin
      const dataToSend = {
        ...formData,
        heure_fin: calculateEndTime(formData.heure_debut),
        type: planningType
      };
      await axios.post('/api/planning/rdv', dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      fetchRdvs();
      fetchStats();
    } catch (error) {
      console.error('Erreur création RDV:', error);
      alert(error.response?.data?.error || 'Erreur lors de la création du RDV');
    }
  };

  const handleEditRdv = (rdv) => {
    setSelectedRdv(rdv);
    setFormData({
      commercial_id: rdv.commercial_id,
      titre: rdv.titre,
      description: rdv.description || '',
      client_nom: rdv.client_nom || '',
      client_telephone: rdv.client_telephone || '',
      client_adresse: rdv.client_adresse || '',
      date_rdv: rdv.date_rdv.split('T')[0],
      heure_debut: rdv.heure_debut.substring(0, 5),
      heure_fin: rdv.heure_fin.substring(0, 5),
      statut: rdv.statut
    });
    setShowEditModal(true);
  };

  const handleUpdateRdv = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      // Calculer automatiquement l'heure de fin
      const dataToSend = {
        ...formData,
        heure_fin: calculateEndTime(formData.heure_debut)
      };
      await axios.put(`/api/planning/rdv/${selectedRdv.id}`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      setSelectedRdv(null);
      fetchRdvs();
      fetchStats();
    } catch (error) {
      console.error('Erreur modification RDV:', error);
      alert(error.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleDeleteRdv = async () => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/planning/rdv/${selectedRdv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      setSelectedRdv(null);
      fetchRdvs();
      fetchStats();
    } catch (error) {
      console.error('Erreur suppression RDV:', error);
    }
  };

  // Obtenir les RDV pour un commercial et une date
  const getRdvsForCell = (commercialId, date) => {
    const dateStr = date.toISOString().split('T')[0];
    return rdvs.filter(r =>
      r.commercial_id === commercialId &&
      r.date_rdv.split('T')[0] === dateStr
    );
  };

  const getStatutClass = (statut) => {
    switch(statut) {
      case 'confirme': return 'rdv-confirme';
      case 'annule': return 'rdv-annule';
      case 'termine': return 'rdv-termine';
      default: return 'rdv-planifie';
    }
  };

  const getStatutLabel = (statut) => {
    switch(statut) {
      case 'planifie': return 'Planifie';
      case 'confirme': return 'Confirme';
      case 'annule': return 'Annule';
      case 'termine': return 'Termine';
      default: return statut;
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="planning-container">
      {/* Header avec stats */}
      <div className="planning-header">
        <div className="planning-title-section">
          <h2>Planning Commercial</h2>
          <p className="planning-subtitle">Agenda partage - Semaine du {formatDate(weekDates[0])} au {formatDate(weekDates[6])}</p>
        </div>

        {stats && (
          <div className="planning-stats">
            <div className="stat-card">
              <span className="stat-number">{stats.aujourdhui}</span>
              <span className="stat-label">Aujourd'hui</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.totalSemaine}</span>
              <span className="stat-label">Cette semaine</span>
            </div>
          </div>
        )}

        {/* Barre de recherche */}
        <div className="planning-search">
          <input
            type="text"
            placeholder="Rechercher un RDV (nom, téléphone, adresse...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="search-input"
          />
          <button onClick={handleSearch} className="search-btn" disabled={searching || searchTerm.length < 2}>
            {searching ? '...' : 'Rechercher'}
          </button>
        </div>

        <div className="planning-nav">
          <button onClick={handlePrevWeek} className="nav-btn">Semaine precedente</button>
          <button onClick={handleToday} className="nav-btn nav-btn-today">Aujourd'hui</button>
          <button onClick={handleNextWeek} className="nav-btn">Semaine suivante</button>
          {(userRole === 'admin' || userId === 2) && (
            <button
              onClick={openEmailModal}
              className="nav-btn nav-btn-email"
            >
              <Mail size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer planning demain
            </button>
          )}
          {userRole === 'admin' && (
            <button
              onClick={() => setShowBlocageListModal(true)}
              className="nav-btn nav-btn-blocage"
            >
              <Ban size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Gerer blocages
            </button>
          )}
        </div>
      </div>

      {/* Boutons navigation rapide vers chaque planning */}
      <div className="planning-nav-buttons">
        {commerciaux.map(c => (
          <button
            key={c.id}
            className="planning-nav-btn"
            style={{ backgroundColor: c.couleur, color: 'white' }}
            onClick={() => {
              const el = document.getElementById(`planning-${c.id}`);
              if (el) {
                const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
                window.scrollTo({ top: y, behavior: 'smooth' });
              }
            }}
          >
            {c.nom}
          </button>
        ))}
      </div>

      {/* Legende commerciaux */}
      <div className="commerciaux-legend">
        {commerciaux.map(c => (
          <div key={c.id} className="legend-item" style={{ borderLeftColor: c.couleur }}>
            <span className="legend-color" style={{ backgroundColor: c.couleur }}></span>
            <span className="legend-name">{c.nom}</span>
            {userRole === 'admin' && (
              <button
                className="btn-edit-commercial"
                onClick={() => {
                  setEditingCommercial(c);
                  setCommercialForm({ nom: c.nom, couleur: c.couleur, email: c.email || '' });
                  setShowCommercialModal(true);
                }}
                title="Modifier le nom"
              >
                <Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
              </button>
            )}
          </div>
        ))}
        <button className="btn-add-rdv-global" onClick={() => {
          setFormData({
            commercial_id: commerciaux[0]?.id || '',
            titre: '',
            description: '',
            client_nom: '',
            client_telephone: '',
            client_adresse: '',
            date_rdv: new Date().toISOString().split('T')[0],
            heure_debut: '09:00',
            heure_fin: '10:00',
            statut: 'planifie'
          });
          setShowModal(true);
        }}>
          + Nouveau RDV
        </button>
        {userRole === 'admin' && (
          <button className="btn-add-rdv-global" style={{ background: '#7b2ff7' }} onClick={() => {
            setEditingCommercial({ id: null });
            setCommercialForm({ nom: '', couleur: '#7b2ff7', email: '' });
            setShowCommercialModal(true);
          }}>
            + Ajouter une colonne
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Chargement du planning...</div>
      ) : (
        <div className="planning-wrapper">
          {/* Un tableau par commercial */}
          {commerciaux.map(commercial => (
            <div key={commercial.id} id={`planning-${commercial.id}`} className="commercial-planning-section">
              <div className="commercial-section-header" style={{ borderLeftColor: commercial.couleur, backgroundColor: commercial.couleur + '15' }}>
                <span className="commercial-section-name" style={{ color: commercial.couleur }}>{commercial.nom}</span>
              </div>

              <div className="planning-grid-slots">
                {/* Header des jours */}
                <div className="slots-header-row">
                  <div className="slot-time-header">Heure</div>
                  {weekDates.slice(0, 5).map((date, idx) => (
                    <div key={idx} className={`slot-day-header ${isToday(date) ? 'today' : ''}`}>
                      <span className="day-name">{joursSemaine[idx]}</span>
                      <span className="day-date">{formatDate(date)}</span>
                    </div>
                  ))}
                </div>

                {/* Lignes par créneau horaire */}
                {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'].map(heure => (
                  <div key={heure} className="slots-row">
                    <div className="slot-time-cell">{heure}</div>
                    {weekDates.slice(0, 5).map((date, idx) => {
                      const dateStr = date.toISOString().split('T')[0];
                      // Trouver TOUS les RDV qui commencent dans ce créneau horaire
                      const slotHour = parseInt(heure.split(':')[0]);
                      const slotRdvs = rdvs.filter(r => {
                        if (r.commercial_id !== commercial.id) return false;
                        if (r.date_rdv.split('T')[0] !== dateStr) return false;
                        const rdvHour = parseInt(r.heure_debut.substring(0, 2));
                        return rdvHour === slotHour;
                      });

                      // Vérifier s'il y a un RDV confirmé sur ce créneau
                      const hasConfirmedRdv = slotRdvs.some(r => r.statut === 'confirme');

                      // Vérifier si le créneau est bloqué
                      const slotBlocked = isSlotBlocked(commercial.id, date, heure);
                      const blocageInfo = slotBlocked ? getBlockageForSlot(commercial.id, date, heure) : null;

                      // Calculer le décalage en fonction des minutes
                      const getMinuteOffset = (rdv, index, total) => {
                        if (!rdv) return 0;
                        // Si plusieurs RDV, les empiler verticalement
                        if (total > 1) {
                          return (index / total) * 100;
                        }
                        const minutes = parseInt(rdv.heure_debut.substring(3, 5));
                        return (minutes / 60) * 100;
                      };

                      return (
                        <div
                          key={idx}
                          className={`slot-cell ${isToday(date) ? 'today' : ''} ${slotRdvs.length > 0 ? 'has-rdv' : ''} ${slotRdvs.length > 1 ? 'has-multiple-rdv' : ''} ${slotBlocked ? 'slot-blocked' : ''}`}
                          onClick={() => !hasConfirmedRdv && !slotBlocked && handleAddRdv(commercial.id, dateStr, heure)}
                        >
                          {slotBlocked ? (
                            <div
                              className={`slot-blocage blocage-${blocageInfo?.motif || 'indisponible'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (userRole === 'admin') {
                                  editBlocage(blocageInfo);
                                }
                              }}
                              title={blocageInfo?.description || getMotifLabel(blocageInfo?.motif)}
                            >
                              <span className="blocage-icon">
                                {blocageInfo?.motif === 'vacances' ? <Palmtree size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> :
                                 blocageInfo?.motif === 'ferme' ? <Lock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> :
                                 blocageInfo?.motif === 'formation' ? <BookOpen size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> : <Ban size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />}
                              </span>
                              <span className="blocage-label">{getMotifLabel(blocageInfo?.motif)}</span>
                            </div>
                          ) : slotRdvs.length > 0 ? (
                            <>
                              {slotRdvs.map((rdv, rdvIndex) => (
                                <div
                                  key={rdv.id}
                                  className={`slot-rdv ${getStatutClass(rdv.statut)} ${slotRdvs.length > 1 ? 'slot-rdv-stacked' : ''}`}
                                  style={{
                                    borderLeftColor: commercial.couleur,
                                    ...(slotRdvs.length === 1 ? {
                                      top: `${getMinuteOffset(rdv, rdvIndex, slotRdvs.length)}%`
                                    } : {}),
                                    zIndex: rdv.statut === 'confirme' ? 10 : 1
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditRdv(rdv);
                                  }}
                                >
                                  <div className="slot-rdv-time">{rdv.heure_debut.substring(0, 5)}</div>
                                  <div className="slot-rdv-titre">{rdv.titre}</div>
                                  {rdv.client_nom && <div className="slot-rdv-client">{rdv.client_nom}</div>}
                                  <div className="slot-rdv-author">Pris par: {rdv.created_by_nom || 'Inconnu'}</div>
                                </div>
                              ))}
                              {/* Bouton + si pas de RDV confirmé */}
                              {!hasConfirmedRdv && (
                                <div className="slot-add-btn" onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddRdv(commercial.id, dateStr, heure);
                                }}>+</div>
                              )}
                            </>
                          ) : (
                            <div className="slot-empty">+</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Résultats de recherche */}
      {showSearchResults && (
        <div className="modal-overlay">
          <div className="modal-content search-results-modal">
            <div className="modal-header">
              <h3>Résultats de recherche : "{searchTerm}"</h3>
              <button className="btn-close" onClick={() => setShowSearchResults(false)}>×</button>
            </div>
            <div className="search-results-list">
              {searchResults.length === 0 ? (
                <p className="no-results">Aucun RDV trouvé</p>
              ) : (
                <>
                  <p className="results-count">{searchResults.length} RDV trouvé(s)</p>
                  {searchResults.map(rdv => (
                    <div key={rdv.id} className="search-result-item" onClick={() => goToRdv(rdv)}>
                      <div className="result-header">
                        <span className="result-date">
                          <Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {new Date(rdv.date_rdv).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="result-time"><Clock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {rdv.heure_debut} - {rdv.heure_fin}</span>
                        <span className="result-status" style={{
                          backgroundColor: rdv.statut === 'confirme' ? '#4caf50' : rdv.statut === 'annule' ? '#f44336' : '#ff9800',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {rdv.statut === 'confirme' ? 'Confirmé' : rdv.statut === 'annule' ? 'Annulé' : 'Planifié'}
                        </span>
                      </div>
                      <div className="result-title"><strong>{rdv.titre}</strong></div>
                      <div className="result-details">
                        <span><User size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {rdv.client_nom || 'N/A'}</span>
                        <span><Phone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {rdv.client_telephone || 'N/A'}</span>
                        <span style={{ color: rdv.commercial_couleur }}>● {rdv.commercial_nom}</span>
                      </div>
                      {rdv.client_adresse && (
                        <div className="result-address"><MapPin size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {rdv.client_adresse}</div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout RDV */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content planning-modal">
            <div className="modal-header">
              <h3>Nouveau Rendez-vous</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    client_nom: data.dirigeant_nom || prev.client_nom,
                    client_adresse: data.adresse || prev.client_adresse,
                    titre: prev.titre || `RDV ${data.entreprise}`,
                    description: prev.description || `Entreprise: ${data.entreprise}${data.dirigeant_qualite ? `\nDirigeant: ${data.dirigeant_nom} (${data.dirigeant_qualite})` : ''}`
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Commercial *</label>
                  <select
                    value={formData.commercial_id}
                    onChange={e => setFormData({...formData, commercial_id: parseInt(e.target.value)})}
                    required
                  >
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={e => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="planifie">Planifie</option>
                    {(userRole === 'admin' || userId === 2) && (
                      <option value="confirme">Confirme</option>
                    )}
                    <option value="annule">Annule</option>
                    <option value="termine">Termine</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre du RDV *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={e => setFormData({...formData, titre: e.target.value})}
                  placeholder="Ex: Presentation produit, Signature contrat..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date_rdv}
                    onChange={e => setFormData({...formData, date_rdv: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure *</label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    required
                  />
                  <small className="form-hint">Duree: 1h</small>
                </div>
              </div>

              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_nom}
                  onChange={e => setFormData({...formData, client_nom: e.target.value})}
                  placeholder="Nom du client ou prospect"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telephone client</label>
                  <input
                    type="tel"
                    value={formData.client_telephone}
                    onChange={e => setFormData({...formData, client_telephone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={formData.client_adresse}
                    onChange={e => setFormData({...formData, client_adresse: e.target.value})}
                    placeholder="Lieu du rendez-vous"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Details du rendez-vous..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Creer le RDV
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Edition RDV */}
      {showEditModal && selectedRdv && (
        <div className="modal-overlay">
          <div className="modal-content planning-modal">
            <div className="modal-header">
              <h3>Modifier le Rendez-vous</h3>
              <button className="btn-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateRdv}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    client_nom: data.dirigeant_nom || prev.client_nom,
                    client_adresse: data.adresse || prev.client_adresse
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Commercial *</label>
                  <select
                    value={formData.commercial_id}
                    onChange={e => setFormData({...formData, commercial_id: parseInt(e.target.value)})}
                    required
                  >
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={e => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="planifie">Planifie</option>
                    {(userRole === 'admin' || userId === 2) && (
                      <option value="confirme">Confirme</option>
                    )}
                    <option value="annule">Annule</option>
                    <option value="termine">Termine</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre du RDV *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={e => setFormData({...formData, titre: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date_rdv}
                    onChange={e => setFormData({...formData, date_rdv: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure *</label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    required
                  />
                  <small className="form-hint">Duree: 1h</small>
                </div>
              </div>

              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_nom}
                  onChange={e => setFormData({...formData, client_nom: e.target.value})}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telephone client</label>
                  <input
                    type="tel"
                    value={formData.client_telephone}
                    onChange={e => setFormData({...formData, client_telephone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={formData.client_adresse}
                    onChange={e => setFormData({...formData, client_adresse: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="rdv-info">
                <small>RDV pris par: <strong>{selectedRdv.created_by_nom || 'Inconnu'}</strong> {selectedRdv.created_at ? `le ${new Date(selectedRdv.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}</small>
              </div>

              <div className="modal-actions">
                {userRole === 'admin' && (
                  <button type="button" className="btn-delete" onClick={handleDeleteRdv}>
                    Supprimer
                  </button>
                )}
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Modifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Edition Commercial */}
      {showCommercialModal && editingCommercial && (
        <div className="modal-overlay">
          <div className="modal-content commercial-modal">
            <div className="modal-header">
              <h3>{editingCommercial.id ? 'Modifier la colonne' : 'Ajouter une colonne'}</h3>
              <button className="btn-close" onClick={() => setShowCommercialModal(false)}>&times;</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const token = localStorage.getItem('token');
                if (editingCommercial.id) {
                  await axios.put(`/api/planning/commerciaux/${editingCommercial.id}`, commercialForm, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                } else {
                  await axios.post('/api/planning/commerciaux', { ...commercialForm, type: planningType }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                }
                setShowCommercialModal(false);
                setEditingCommercial(null);
                fetchCommerciaux();
              } catch (error) {
                console.error('Erreur enregistrement colonne:', error);
                alert('Erreur lors de l\'enregistrement');
              }
            }}>
              <div className="form-group">
                <label>Nom du commercial</label>
                <input
                  type="text"
                  value={commercialForm.nom}
                  onChange={e => setCommercialForm({...commercialForm, nom: e.target.value})}
                  placeholder="Nom du commercial"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email du commercial</label>
                <input
                  type="email"
                  value={commercialForm.email}
                  onChange={e => setCommercialForm({...commercialForm, email: e.target.value})}
                  placeholder="email@exemple.com"
                />
                <small className="form-hint">Pour recevoir le planning quotidien par email</small>
              </div>

              <div className="form-group">
                <label>Couleur</label>
                <div className="color-picker-row">
                  <input
                    type="color"
                    value={commercialForm.couleur}
                    onChange={e => setCommercialForm({...commercialForm, couleur: e.target.value})}
                    className="color-picker"
                  />
                  <span className="color-value">{commercialForm.couleur}</span>
                </div>
                <div className="color-presets">
                  {['#667eea', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14', '#20c997'].map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`color-preset ${commercialForm.couleur === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setCommercialForm({...commercialForm, couleur: color})}
                    />
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                {editingCommercial.id && userRole === 'admin' && (
                  <button type="button" className="btn-delete" onClick={async () => {
                    if (!window.confirm('Supprimer cette colonne ? Tous ses RDV seront également supprimés.')) return;
                    try {
                      const token = localStorage.getItem('token');
                      await axios.delete(`/api/planning/commerciaux/${editingCommercial.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setShowCommercialModal(false);
                      setEditingCommercial(null);
                      fetchCommerciaux();
                      fetchRdvs();
                    } catch (error) {
                      console.error('Erreur suppression colonne:', error);
                      alert('Erreur lors de la suppression');
                    }
                  }}>
                    Supprimer
                  </button>
                )}
                <button type="button" className="btn-cancel" onClick={() => setShowCommercialModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Sélection commerciaux pour email */}
      {showEmailModal && (
        <div className="modal-overlay">
          <div className="modal-content email-select-modal">
            <div className="modal-header">
              <h3><Mail size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer le planning de demain</h3>
              <button className="btn-close" onClick={() => setShowEmailModal(false)}>&times;</button>
            </div>
            <p className="email-modal-subtitle">Sélectionnez les commerciaux et renseignez leur adresse email.<br/>
            <small>Seuls les RDV avec le statut <strong>"Confirmé"</strong> seront envoyés.</small></p>

            <div className="email-commercial-list">
              {emailSelections.map(selection => (
                <div
                  key={selection.id}
                  className={`email-commercial-item ${selection.selected ? 'selected' : ''}`}
                  style={{ borderLeftColor: selection.couleur }}
                >
                  <label className="email-commercial-checkbox">
                    <input
                      type="checkbox"
                      checked={selection.selected}
                      onChange={() => toggleCommercialSelection(selection.id)}
                    />
                    <span className="commercial-name-label">{selection.nom}</span>
                  </label>
                  <input
                    type="email"
                    className="email-commercial-input"
                    value={selection.email}
                    onChange={e => updateCommercialEmail(selection.id, e.target.value)}
                    placeholder="email@exemple.com"
                    disabled={!selection.selected}
                  />
                </div>
              ))}
            </div>

            <div className="email-modal-info">
              <span>{emailSelections.filter(s => s.selected).length} commercial(s) sélectionné(s)</span>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowEmailModal(false)}>
                Annuler
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={sendSelectedEmails}
                disabled={sendingEmails || emailSelections.filter(s => s.selected && s.email).length === 0}
              >
                {sendingEmails ? 'Envoi en cours...' : <><Mail size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Résultat envoi emails */}
      {emailResult && (
        <div className="modal-overlay">
          <div className="modal-content email-result-modal">
            <div className="modal-header">
              <h3>{emailResult.success ? <><CheckCircle size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Emails envoyés</> : <><XCircle size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Erreur</>}</h3>
              <button className="btn-close" onClick={() => setEmailResult(null)}>&times;</button>
            </div>

            {emailResult.success ? (
              <>
                <p className="email-result-date">Planning du <strong>{emailResult.date}</strong></p>

                {emailResult.sent && emailResult.sent.length > 0 && (
                  <div className="email-result-section">
                    <h4><Mail size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyés avec succès ({emailResult.sent.length})</h4>
                    <ul className="email-result-list">
                      {emailResult.sent.map((item, idx) => (
                        <li key={idx} className="email-success">
                          <strong>{item.nom}</strong> - {item.email}
                          <span className="rdv-count">{item.rdvCount} RDV</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {emailResult.failed && emailResult.failed.length > 0 && (
                  <div className="email-result-section">
                    <h4><AlertTriangle size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Echecs ({emailResult.failed.length})</h4>
                    <ul className="email-result-list">
                      {emailResult.failed.map((item, idx) => (
                        <li key={idx} className="email-failed">
                          <strong>{item.nom}</strong> - {item.email}
                          <span className="error-msg">{item.error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="email-error-msg">{emailResult.error}</p>
            )}

            <div className="modal-actions">
              <button className="btn-submit" onClick={() => setEmailResult(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Liste des Blocages */}
      {showBlocageListModal && (
        <div className="modal-overlay">
          <div className="modal-content blocage-list-modal">
            <div className="modal-header">
              <h3><Ban size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Gestion des Blocages</h3>
              <button className="btn-close" onClick={() => setShowBlocageListModal(false)}>&times;</button>
            </div>
            <p className="blocage-modal-subtitle">
              Gerez les periodes de fermeture, vacances et indisponibilites des commerciaux.
            </p>

            <button className="btn-add-blocage" onClick={() => {
              setShowBlocageListModal(false);
              openBlocageModal();
            }}>
              + Nouveau blocage
            </button>

            <div className="blocages-list">
              {blocages.length === 0 ? (
                <div className="no-blocages">
                  <p>Aucun blocage configure pour cette periode.</p>
                </div>
              ) : (
                blocages.map(blocage => (
                  <div key={blocage.id} className={`blocage-item blocage-${blocage.motif}`}>
                    <div className="blocage-item-header">
                      <span className="blocage-item-icon">
                        {blocage.motif === 'vacances' ? <Palmtree size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> :
                         blocage.motif === 'ferme' ? <Lock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> :
                         blocage.motif === 'formation' ? <BookOpen size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> : <Ban size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />}
                      </span>
                      <span className="blocage-item-motif">{getMotifLabel(blocage.motif)}</span>
                      {blocage.commercial_nom ? (
                        <span className="blocage-item-commercial" style={{ color: blocage.commercial_couleur }}>
                          {blocage.commercial_nom}
                        </span>
                      ) : (
                        <span className="blocage-item-commercial blocage-tous">Tous les commerciaux</span>
                      )}
                    </div>
                    <div className="blocage-item-dates">
                      <span>Du {new Date(blocage.date_debut).toLocaleDateString('fr-FR')}</span>
                      <span> au {new Date(blocage.date_fin).toLocaleDateString('fr-FR')}</span>
                      {blocage.heure_debut && blocage.heure_fin && (
                        <span className="blocage-item-heures">
                          ({blocage.heure_debut.substring(0, 5)} - {blocage.heure_fin.substring(0, 5)})
                        </span>
                      )}
                    </div>
                    {blocage.description && (
                      <div className="blocage-item-description">{blocage.description}</div>
                    )}
                    <div className="blocage-item-actions">
                      <button className="btn-edit-blocage" onClick={() => {
                        setShowBlocageListModal(false);
                        editBlocage(blocage);
                      }}>Modifier</button>
                      <button className="btn-delete-blocage" onClick={() => deleteBlocage(blocage.id)}>
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowBlocageListModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création/Edition Blocage */}
      {showBlocageModal && (
        <div className="modal-overlay">
          <div className="modal-content blocage-modal">
            <div className="modal-header">
              <h3>{editingBlocage ? 'Modifier le blocage' : 'Nouveau blocage'}</h3>
              <button className="btn-close" onClick={() => setShowBlocageModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleBlocageSubmit}>
              <div className="form-group">
                <label>Commercial</label>
                <select
                  value={blocageForm.commercial_id}
                  onChange={e => setBlocageForm({...blocageForm, commercial_id: e.target.value})}
                >
                  <option value="">Tous les commerciaux</option>
                  {commerciaux.map(c => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
                <small className="form-hint">Laissez vide pour bloquer tous les commerciaux</small>
              </div>

              <div className="form-group">
                <label>Motif *</label>
                <select
                  value={blocageForm.motif}
                  onChange={e => setBlocageForm({...blocageForm, motif: e.target.value})}
                  required
                >
                  <option value="vacances">🏖️ Vacances</option>
                  <option value="indisponible">🚫 Indisponible</option>
                  <option value="ferme">🔒 Ferme</option>
                  <option value="formation">📚 Formation</option>
                  <option value="autre">📝 Autre</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date debut *</label>
                  <input
                    type="date"
                    value={blocageForm.date_debut}
                    onChange={e => setBlocageForm({...blocageForm, date_debut: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date fin *</label>
                  <input
                    type="date"
                    value={blocageForm.date_fin}
                    onChange={e => setBlocageForm({...blocageForm, date_fin: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Heure debut (optionnel)</label>
                  <input
                    type="time"
                    value={blocageForm.heure_debut}
                    onChange={e => setBlocageForm({...blocageForm, heure_debut: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure fin (optionnel)</label>
                  <input
                    type="time"
                    value={blocageForm.heure_fin}
                    onChange={e => setBlocageForm({...blocageForm, heure_fin: e.target.value})}
                  />
                </div>
              </div>
              <small className="form-hint">Laissez vide pour bloquer la journee entiere</small>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={blocageForm.description}
                  onChange={e => setBlocageForm({...blocageForm, description: e.target.value})}
                  rows="2"
                  placeholder="Ex: Vacances d'ete, Formation CRM..."
                />
              </div>

              <div className="modal-actions">
                {editingBlocage && (
                  <button type="button" className="btn-delete" onClick={() => deleteBlocage(editingBlocage.id)}>
                    Supprimer
                  </button>
                )}
                <button type="button" className="btn-cancel" onClick={() => setShowBlocageModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  {editingBlocage ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Planning;
